import React, { useState, useEffect } from 'react';
import { BarChart3, Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'optimizedPCs', label: 'PC optimisés', hint: 'Affiché comme "+N" (en-tête, menu, CTA, services)', step: '1' },
  { key: 'reviewsCount', label: 'Avis vérifiés', hint: 'Affiché sur la page Avis ("+N avis vérifiés")', step: '1' },
  { key: 'rating', label: 'Note moyenne /5', hint: 'Ex : 4.9 (en-tête, page Avis, CTA)', step: '0.1' },
  { key: 'satisfiedPercent', label: 'Clients satisfaits (%)', hint: 'Ex : 99', step: '1' },
  { key: 'totalMembers', label: 'Membres Discord', hint: 'Nombre de membres affiché', step: '1' },
];

const SiteStatsTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/site-stats`)
      .then((r) => r.json())
      .then((d) => setStats(Object.fromEntries(Object.entries(d).map(([k, v]) => [k, String(v)]))))
      .catch(() => setError('Erreur de chargement'));
  }, []);

  const save = async () => {
    setError('');
    const parsed = {};
    for (const f of FIELDS) {
      const raw = String(stats[f.key] ?? '').replace(',', '.');
      const n = parseFloat(raw);
      if (isNaN(n) || n < 0) { setError(`Valeur invalide pour "${f.label}"`); return; }
      parsed[f.key] = n;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/site-stats`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stats: parsed }),
      });
      if (!res.ok) throw new Error('save failed');
      const d = await res.json();
      setStats(Object.fromEntries(Object.entries(d).map(([k, v]) => [k, String(v)])));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400" data-testid="site-stats-loading">
        {error ? <span className="text-red-400">{error}</span> : <Loader2 className="w-6 h-6 animate-spin" />}
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="site-stats-tab">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-5 h-5 text-cyan-400" />
        <h2 className="text-xl font-bold text-white">Chiffres du site</h2>
      </div>
      <p className="text-gray-400 text-sm mb-6">
        Ces chiffres s'affichent partout sur le site (en-tête, menu, page Avis, bas de page). Modifie-les ici,
        ils se mettent à jour immédiatement pour tous les visiteurs.
      </p>

      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <label className="block text-sm font-semibold text-white mb-1" htmlFor={`stat-${f.key}`}>
              {f.label}
            </label>
            <p className="text-gray-500 text-xs mb-2">{f.hint}</p>
            <input
              id={`stat-${f.key}`}
              data-testid={`site-stat-input-${f.key}`}
              type="number"
              step={f.step}
              min="0"
              value={stats[f.key]}
              onChange={(e) => setStats((s) => ({ ...s, [f.key]: e.target.value }))}
              className="w-40 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
            />
          </div>
        ))}
      </div>

      {error && <p className="text-red-400 text-sm mt-4" data-testid="site-stats-error">{error}</p>}

      <button
        onClick={save}
        disabled={saving}
        data-testid="site-stats-save-btn"
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saved ? 'Enregistré ✓' : 'Enregistrer'}
      </button>
    </div>
  );
};

export default SiteStatsTab;
